import { useCallback, useEffect, useRef, useState } from 'react';
import { requestJSON } from '../lib/request';
import { previewSnapshotKey } from '../photo/useAIPreview';

export const conceptDesignKey = design => previewSnapshotKey(design ? { ...design, photo_overlay: null } : null);

export function useConceptImages(configurationId, saveDesign, captureDesign, currentDesign) {
  const [items, setItems] = useState([]), [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState(''), [preparing, setPreparing] = useState(false);
  const [notes, setNotes] = useState(''), [scene, setScene] = useState('exterior'), [houseStyle, setHouseStyle] = useState('');
  const busyRef = useRef(false), requestId = useRef(null);
  const active = items.find(item => ['queued', 'generating'].includes(item.status));
  const resumable = items[0]?.status === 'failed' && items[0]?.background_available && conceptDesignKey(items[0].snapshot) === conceptDesignKey(currentDesign) ? items[0] : null;
  const read = useCallback(async signal => {
    if (!configurationId) return;
    const result = await requestJSON(`/configurations/${configurationId}/concept-images`, { signal });
    if (signal?.aborted) return;
    setItems(previous => [...result, ...previous.filter(item => item.configuration_id === configurationId && ['queued', 'generating'].includes(item.status) && !result.some(row => row.id === item.id))]);
    setError('');
  }, [configurationId]);
  useEffect(() => {
    const controller = new AbortController(); setItems([]); setSelectedId(null); setError('');
    read(controller.signal).catch(err => { if (!controller.signal.aborted) setError(err.message); });
    return () => controller.abort();
  }, [read]);
  useEffect(() => {
    if (!active) return;
    const controller = new AbortController(); let fetching = false;
    const timer = setInterval(async () => {
      if (fetching) return; fetching = true;
      try { await read(controller.signal); } catch (err) { if (!controller.signal.aborted) setError(err.message); } finally { fetching = false; }
    }, 2000);
    return () => { clearInterval(timer); controller.abort(); };
  }, [active?.id, read]); // eslint-disable-line react-hooks/exhaustive-deps
  const generate = async () => {
    if (busyRef.current || active) return;
    busyRef.current = true; setPreparing(true); setError('');
    try {
      const reference = await captureDesign(scene);
      const id = await saveDesign();
      requestId.current ||= crypto.randomUUID();
      const body = new FormData(); body.append('request_id', requestId.current); body.append('scene_notes', notes); body.append('scene', scene); body.append('house_style', houseStyle.trim());
      body.append('studio_reference', reference.blob, 'exact-studio-model.png'); body.append('reference_snapshot', JSON.stringify(reference.snapshot));
      const job = await requestJSON(`/configurations/${id}/concept-images`, { method: 'POST', body, timeoutMs: 120000 });
      setItems(previous => [job, ...previous.filter(item => item.id !== job.id)]); setSelectedId(null); requestId.current = null;
    } catch (err) { setError(err.message); } finally { setPreparing(false); busyRef.current = false; }
  };
  const resume = async () => {
    if (!resumable || busyRef.current || active) return;
    busyRef.current = true; setPreparing(true); setError('');
    try {
      const job = await requestJSON(`/concept-images/${resumable.id}/resume`, { method: 'POST' });
      setItems(previous => [job, ...previous.filter(item => item.id !== job.id)]); setSelectedId(null);
    } catch (err) { setError(err.message); } finally { setPreparing(false); busyRef.current = false; }
  };
  return { items, active, resumable, resume, selected: items.find(item => item.id === selectedId) || items.find(item => item.status === 'succeeded'), select: setSelectedId, error, preparing, busy: preparing || !!active, notes, setNotes, scene, setScene, houseStyle, setHouseStyle, generate, refresh: () => read().catch(err => setError(err.message)) };
}